import { z } from 'zod';

const objectId = z.string().regex(/^[0-9a-fA-F]{24}$/);
const slug = z.string().regex(/^[a-z0-9][a-z0-9-]{1,40}$/);

const UserMatchSchema = z.object({
    attribute: z.enum(['sAMAccountName', 'userPrincipalName', 'email', 'userName', 'externalId']).optional(),
    caseInsensitive: z.boolean().optional(),
});

const AccessSchema = z.object({
    requireImported: z.boolean().optional(),
    allowedStatuses: z.array(z.enum(['active', 'inactive', 'pending', 'suspended'])).optional(),
});

const IPV4_OCTET = '(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)';
const IPV4_ADDRESS = new RegExp(`^${IPV4_OCTET}(?:\\.${IPV4_OCTET}){3}$`);

/** IPv4 address or IPv4 CIDR. Matches the portal handoff caller check. */
export function isPortalIpAllowRule(value) {
    const entry = String(value || '').trim();
    const slash = entry.indexOf('/');
    if (slash === -1) return IPV4_ADDRESS.test(entry);
    if (entry.indexOf('/', slash + 1) !== -1) return false;
    const bitsRaw = entry.slice(slash + 1);
    if (!/^\d{1,2}$/.test(bitsRaw)) return false;
    const bits = Number(bitsRaw);
    return IPV4_ADDRESS.test(entry.slice(0, slash)) && bits >= 0 && bits <= 32;
}

const PortalHandoffSchema = z.object({
    requestField: z.string().min(1).optional(),
    responseShape: z.enum(['gsb', 'standard']).optional(),
    apiKeyHeader: z.string().min(1).optional(),
    apiKey: z.string().min(8).optional(),
    ipAllowList: z.array(z.string().trim().min(1).refine(isPortalIpAllowRule)).optional(),
    tokenTtlSeconds: z.number().int().min(15).max(600).optional(),
    redirectParam: z.string().min(1).optional(),
});

const OidcSchema = z.object({
    issuer: z.string().url().optional(),
    clientId: z.string().min(1).optional(),
    clientSecret: z.string().optional(),
    authorizationUrl: z.string().url().optional(),
    tokenUrl: z.string().url().optional(),
    userInfoUrl: z.string().url().optional(),
    jwksUri: z.string().url().optional(),
    scopes: z.array(z.string()).optional(),
    claimForMatch: z.enum(['preferred_username', 'email', 'sub']).optional(),
    usePKCE: z.boolean().optional(),
});

export const CreateIdentityProviderSchema = z.object({
    key: slug,
    name: z.string().min(1),
    type: z.enum(['portal-handoff', 'oidc']),
    enabled: z.boolean().optional(),
    isDefault: z.boolean().optional(),
    userMatch: UserMatchSchema.optional(),
    access: AccessSchema.optional(),
    postLoginPath: z.string().min(1).optional(),
    portalHandoff: PortalHandoffSchema.optional(),
    oidc: OidcSchema.optional(),
    customFields: z.record(z.unknown()).optional(),
});

export const UpdateIdentityProviderSchema = CreateIdentityProviderSchema.partial().omit({ key: true, type: true });

export const SsoExchangeSchema = z.object({
    code: z.string().min(16),
});

export { objectId as IdentityProviderIdSchema };
